/**
 * /unit/:unitId/payment-links/:linkId – one payment link: share panel, stats, live payments
 * table, settings summary and activity.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Archive,
  ArrowLeft,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Gift,
  Hash,
  Loader2,
  Pencil,
  Power,
  Receipt,
  TrendingUp,
  Wallet,
  Clock,
} from 'lucide-react';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { PaymentLinkPayment } from '@masonic-tools/shared-types';
import {
  useCanManagePaymentLinks,
  useNewIds,
  useNow,
  usePaymentLink,
  usePaymentLinkActivity,
  usePaymentLinkPayments,
  usePaymentLinkSetup,
} from '@/hooks/usePaymentLinks';
import {
  amountRuleLabel,
  formatDateTime,
  formatPence,
  METHOD_LABELS,
  relativeDays,
} from '@/lib/unit-payment-links';
import { PaymentLinkStatusBadge, ReferenceChip } from '@/components/payment-links/PaymentLinkStatusBadge';
import { PaymentLinkFormDialog } from '@/components/payment-links/PaymentLinkFormDialog';
import { copyLink, PaymentLinkQrDialog, useQrPreview, usePaymentLinkDownloads } from '@/components/payment-links/PaymentLinkQrDialog';
import { PaymentsTable } from '@/components/payment-links/PaymentsTable';
import { PaymentDetailDialog } from '@/components/payment-links/PaymentDetailDialog';
import { PaymentExportDialog } from '@/components/payment-links/PaymentExportDialog';
import { ActionsMenu } from '@/components/payment-links/ActionsMenu';
import { confirmAndSetLinkStatus } from '@/components/payment-links/linkActions';

function Stat({ label, value, sub, icon: Icon }: { label: string; value: string; sub?: string; icon: typeof Wallet }) {
  return (
    <Card className="min-w-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="truncate text-lg font-bold tabular-nums sm:text-xl">{value}</div>
        {sub ? <p className="truncate text-[11px] text-muted-foreground">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 py-2 text-sm sm:grid-cols-[200px_1fr]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

export default function UnitPaymentLinkDetailPage() {
  const { unitId = '', linkId = '' } = useParams<{ unitId: string; linkId: string }>();
  const navigate = useNavigate();
  const now = useNow();
  const canManage = useCanManagePaymentLinks(unitId);
  const setup = usePaymentLinkSetup(unitId);
  const { link, loading, notFound } = usePaymentLink(canManage ? unitId : undefined, linkId);
  const { payments, loading: paymentsLoading } = usePaymentLinkPayments(canManage ? unitId : undefined, linkId);
  const activity = usePaymentLinkActivity(canManage ? unitId : undefined, linkId);
  const newIds = useNewIds(
    payments.filter((p) => p.status === 'paid').map((p) => p.id),
    !paymentsLoading,
  );

  const [editOpen, setEditOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [selected, setSelected] = useState<PaymentLinkPayment | null>(null);
  const [exportFiltered, setExportFiltered] = useState<PaymentLinkPayment[] | null>(null);
  const qrThumb = useQrPreview(link?.shortUrl, 240);
  const { busy, downloadPng, downloadSvg, downloadPoster } = usePaymentLinkDownloads(link, setup.unitName);

  // Keep the open payment sheet in sync with live updates.
  const selectedLive = selected ? payments.find((p) => p.id === selected.id) || selected : null;
  const closeSelected = useCallback(() => setSelected(null), []);

  const stats = useMemo(() => {
    if (!link) return null;
    const avg = link.paymentCount > 0 ? Math.round(link.totalCollectedPence / link.paymentCount) : 0;
    return { avg };
  }, [link]);

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Header />
        <main className="flex-1 py-8">
          <DashboardContentContainer>
            <Alert variant="warning">
              <AlertDescription>Only a Steward, Secretary or Treasurer can view payment links and payer details.</AlertDescription>
            </Alert>
          </DashboardContentContainer>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </main>
      </div>
    );
  }

  if (notFound || !link) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Header />
        <main className="flex-1 py-8">
          <DashboardContentContainer>
            <Alert variant="destructive">
              <AlertDescription>This payment link could not be found.</AlertDescription>
            </Alert>
            <Button className="mt-4" variant="outline" onClick={() => navigate(`/unit/${unitId}/payment-links`)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> All payment links
            </Button>
          </DashboardContentContainer>
        </main>
      </div>
    );
  }

  const expiryText = link.expiresAt ? `Expires ${formatDateTime(link.expiresAt)}` : 'No expiry';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header />
      <main className="flex w-full min-w-0 flex-1 overflow-x-hidden py-6 sm:py-8">
        <DashboardContentContainer>
          {/* Header */}
          <Button variant="ghost" size="sm" className="mb-2 px-3" onClick={() => navigate(`/unit/${unitId}/payment-links`)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> All payment links
          </Button>
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="break-words text-2xl font-bold tracking-tight sm:text-3xl">{link.title}</h1>
                <PaymentLinkStatusBadge link={link} nowMs={now} availableMethods={setup.loading ? undefined : setup.availableMethods} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <ReferenceChip reference={link.reference} />
                <span>{METHOD_LABELS[link.paymentMethod]}</span>
                <span>· {link.feeMode === 'on_top' ? 'Fee added on top' : 'Fee included'}</span>
                <span>· {amountRuleLabel(link)}</span>
                <span>· {expiryText}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {link.status !== 'archived' ? (
                <Button variant="outline" onClick={() => setEditOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </Button>
              ) : null}
              {link.status === 'active' ? (
                <Button variant="outline" onClick={() => void confirmAndSetLinkStatus(link, 'disabled')}>
                  <Power className="mr-2 h-4 w-4" /> Disable
                </Button>
              ) : (
                <Button variant="outline" onClick={() => void confirmAndSetLinkStatus(link, 'active')}>
                  <Power className="mr-2 h-4 w-4" /> Enable
                </Button>
              )}
              <ActionsMenu
                items={[
                  { label: 'Open public page', icon: ExternalLink, onSelect: () => window.open(link.targetUrl || link.shortUrl, '_blank', 'noopener') },
                  {
                    label: 'Archive',
                    icon: Archive,
                    destructive: true,
                    hidden: link.status === 'archived',
                    onSelect: () => void confirmAndSetLinkStatus(link, 'archived'),
                  },
                ]}
              />
            </div>
          </div>

          {/* Share panel */}
          <Card className="mb-6">
            <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-5">
              <button
                type="button"
                onClick={() => setQrOpen(true)}
                className="mx-auto h-28 w-28 shrink-0 rounded-md border border-border bg-white p-1 transition-shadow hover:shadow-md sm:mx-0"
                aria-label="Open QR code"
              >
                {qrThumb ? <img src={qrThumb} alt="" className="h-full w-full" /> : null}
              </button>
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <code className="flex-1 break-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm sm:text-base">{link.shortUrl}</code>
                  <Button onClick={() => copyLink(link.shortUrl)}>
                    <Copy className="mr-2 h-4 w-4" /> Copy
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={downloadPng} disabled={!!busy}>
                    <Download className="mr-2 h-4 w-4" /> PNG
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadSvg} disabled={!!busy}>
                    <Download className="mr-2 h-4 w-4" /> SVG
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadPoster} disabled={!!busy}>
                    {busy === 'poster' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} Printable poster
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Stat label="Collected" value={formatPence(link.totalCollectedPence)} icon={Wallet} />
            <Stat label="Received after fees" value={formatPence(link.totalNetPence)} icon={Receipt} />
            <Stat label="Payments" value={String(link.paymentCount)} icon={Hash} />
            <Stat label="Average gift" value={formatPence(stats?.avg ?? 0)} icon={TrendingUp} />
            <Stat label="Gift Aid eligible" value={formatPence(link.giftAidEligiblePence)} sub={`${link.giftAidCount} declaration${link.giftAidCount === 1 ? '' : 's'}`} icon={Gift} />
            <Stat label="Last payment" value={link.lastPaymentAt ? relativeDays(link.lastPaymentAt.getTime(), now) : '—'} sub={link.lastPaymentAt ? formatDateTime(link.lastPaymentAt) : 'No payments yet'} icon={Clock} />
          </div>

          <Tabs defaultValue="payments">
            <TabsList>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="payments" className="mt-4">
              <PaymentsTable payments={payments} loading={paymentsLoading} highlightIds={newIds} onSelect={setSelected} onExport={(filtered) => setExportFiltered(filtered)} />
            </TabsContent>

            <TabsContent value="settings" className="mt-4">
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <dl className="divide-y divide-border">
                    <SummaryRow label="Description">{link.description || <span className="text-muted-foreground">None</span>}</SummaryRow>
                    <SummaryRow label="Reference">
                      <ReferenceChip reference={link.reference} />
                    </SummaryRow>
                    <SummaryRow label="Amount">
                      {amountRuleLabel(link)}
                      {link.suggestedAmountsPence.length ? ` · suggested ${link.suggestedAmountsPence.map((p) => formatPence(p)).join(', ')}` : ''}
                    </SummaryRow>
                    <SummaryRow label="Payment method">{METHOD_LABELS[link.paymentMethod]}</SummaryRow>
                    <SummaryRow label="Fee">
                      {link.feeMode === 'on_top' ? 'Added on top of the payment' : 'Included in the payment'} ({formatPence(setup.feePence[link.paymentMethod])} per payment)
                    </SummaryRow>
                    <SummaryRow label="Gift Aid">{link.giftAidEnabled ? 'Declarations allowed' : 'Off'}</SummaryRow>
                    <SummaryRow label="Thank-you message">{link.thankYouMessage || <span className="text-muted-foreground">None</span>}</SummaryRow>
                    <SummaryRow label="Expiry">{expiryText}</SummaryRow>
                    <SummaryRow label="Notifications">
                      {link.notifications.mode === 'each' ? 'Email on every payment' : link.notifications.mode === 'daily' ? 'Daily summary' : 'Off'}
                      {link.notifications.extraRecipients.length ? ` · also ${link.notifications.extraRecipients.join(', ')}` : ''}
                      {link.expiresAt ? (link.notifications.expiryReminder ? ' · reminder 3 days before expiry' : ' · no expiry reminder') : ''}
                    </SummaryRow>
                    <SummaryRow label="Credits">{setup.creditsPerPayment} credit{setup.creditsPerPayment === 1 ? '' : 's'} per completed payment</SummaryRow>
                    <SummaryRow label="Created">
                      {formatDateTime(link.createdAt)}
                      {link.createdByName ? ` by ${link.createdByName}` : ''}
                    </SummaryRow>
                    <SummaryRow label="Full link">
                      <span className="break-all font-mono text-xs">{link.targetUrl}</span>
                    </SummaryRow>
                  </dl>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="mt-4">
              <Card>
                <CardContent className="p-4 sm:p-6">
                  {activity.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No activity yet.</p>
                  ) : (
                    <ol className="space-y-3 border-l-2 border-border pl-4">
                      {activity.map((a) => (
                        <li key={a.id} className="relative text-sm">
                          <span className="absolute -left-[1.4rem] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" aria-hidden />
                          <p>{a.message}</p>
                          <p className="text-xs text-muted-foreground">{formatDateTime(a.createdAt)}</p>
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </DashboardContentContainer>
      </main>

      <PaymentLinkFormDialog unitId={unitId} open={editOpen} onOpenChange={setEditOpen} setup={setup} link={link} />
      <PaymentLinkQrDialog link={link} unitName={setup.unitName} open={qrOpen} onOpenChange={setQrOpen} />
      <PaymentDetailDialog payment={selectedLive} unitName={setup.unitName} onClose={closeSelected} />
      {exportFiltered ? (
        <PaymentExportDialog
          open
          onOpenChange={(o) => !o && setExportFiltered(null)}
          unitId={unitId}
          unitName={setup.unitName}
          title={link.title}
          reference={link.reference}
          allPayments={payments}
          filteredPayments={exportFiltered}
          linkTitles={{ [link.id]: link.title }}
        />
      ) : null}
    </div>
  );
}
